<template>
  <div class="image-upload-wrapper">
    <el-upload
      class="image-uploader"
      action="#"
      :show-file-list="false"
      :before-upload="beforeUpload"
      :http-request="handleUpload"
    >
      <div
        v-if="imageUrl"
        class="image-preview"
        :style="{ width: width + 'px', height: height + 'px' }"
      >
        <img
          :src="imageUrl"
          :alt="alt"
        >
        <div class="upload-overlay">
          <el-icon><Edit /></el-icon>
        </div>
      </div>
      <div
        v-else
        class="image-upload-placeholder"
        :style="{
          width: width + 'px',
          height: height + 'px',
          backgroundImage: placeholder ? `url(${placeholder})` : 'none'
        }"
      >
        <div
          v-if="!placeholder"
          class="upload-icon"
        >
          <svg
            viewBox="0 0 80 60"
            class="upload-icon-svg"
          >
            <rect
              x="5"
              y="5"
              width="70"
              height="50"
              rx="3"
              fill="#e8f4ff"
              stroke="#4a9eff"
              stroke-width="1.5"
            />
            <circle
              cx="25"
              cy="25"
              r="8"
              fill="#4a9eff"
              opacity="0.3"
            />
            <rect
              x="40"
              y="15"
              width="30"
              height="3"
              rx="1.5"
              fill="#4a9eff"
              opacity="0.3"
            />
            <rect
              x="40"
              y="22"
              width="25"
              height="3"
              rx="1.5"
              fill="#4a9eff"
              opacity="0.3"
            />
            <rect
              x="40"
              y="29"
              width="20"
              height="3"
              rx="1.5"
              fill="#4a9eff"
              opacity="0.3"
            />
            <circle
              cx="40"
              cy="35"
              r="12"
              fill="#4a9eff"
              opacity="0.15"
            />
            <path
              d="M 40 30 L 35 38 L 40 36 L 45 38 Z"
              fill="#4a9eff"
              opacity="0.4"
            />
          </svg>
        </div>
      </div>
    </el-upload>
    <div
      v-if="label"
      class="upload-label"
    >
      {{ label }}
    </div>
  </div>
</template>

<script>
import { ElMessage, ElLoading } from 'element-plus'
import { Edit } from '@element-plus/icons-vue'
import { uploadFile } from '@/api/utils/file'

export default {
  name: 'ImageUpload',
  components: {
    Edit
  },
  props: {
    // 图片URL（用于显示已上传的图片）
    modelValue: {
      type: String,
      default: ''
    },
    // 宽度（像素）
    width: {
      type: Number,
      default: 200
    },
    // 高度（像素）
    height: {
      type: Number,
      default: 130
    },
    // 图片alt属性
    alt: {
      type: String,
      default: '图片'
    },
    // 上传标签文字
    label: {
      type: String,
      default: ''
    },
    // 最大文件大小（MB）
    maxSize: {
      type: Number,
      default: 10
    },
    // 允许的文件类型
    accept: {
      type: String,
      default: 'image/*'
    },
    // 背景占位图片（当没有上传图片时显示）
    placeholder: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'upload-success', 'upload-error'],
  computed: {
    imageUrl: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    }
  },
  methods: {
    /**
     * 上传前的验证
     */
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLtMaxSize = file.size / 1024 / 1024 < this.maxSize

      if (!isImage) {
        ElMessage.error('只能上传图片文件!')
        return false
      }
      if (!isLtMaxSize) {
        ElMessage.error(`图片大小不能超过 ${this.maxSize}MB!`)
        return false
      }
      return true
    },

    /**
     * 自定义上传
     */
    async handleUpload({ file }) {
      try {
        const formData = new FormData()
        formData.append('file', file)
        
        // 显示加载提示
        const loading = ElLoading.service({
          lock: true,
          text: '上传中...',
          background: 'rgba(0, 0, 0, 0.7)'
        })

        try {
          // 调用上传API
          const response = await uploadFile(formData)
          loading.close()
          
          if (response && response.code === 200) {
            const imageUrl = response.data?.originalUrl
            if (imageUrl) {
              // 更新图片URL
              this.imageUrl = imageUrl
              
              // 触发上传成功事件
              this.$emit('upload-success', imageUrl)
              
              ElMessage.success('图片上传成功')
            } else {
              ElMessage.error('图片上传失败，未获取到图片地址')
              this.$emit('upload-error', new Error('未获取到图片地址'))
            }
          } else {
            ElMessage.error(response.msg || '图片上传失败')
            this.$emit('upload-error', new Error(response.msg || '图片上传失败'))
          }
        } catch (apiError) {
          loading.close()
          throw apiError
        }
      } catch (error) {
        console.error('图片上传失败:', error)
        ElMessage.error(error.message || '图片上传失败，请重试')
        this.$emit('upload-error', error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.image-upload-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  .image-uploader {
    :deep(.el-upload) {
      display: block;
    }
  }

  .image-upload-placeholder {
    border-radius: 8px;
    background-color: #fafafa;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      border-color: #1563e8;
      opacity: 0.9;
    }

    .upload-icon {
      .upload-icon-svg {
        width: 80px;
        height: 60px;
      }
    }
  }

  .image-preview {
    box-sizing: border-box;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    cursor: pointer;
    border: 2px solid #d9d9d9;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .upload-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s;

      :deep(i) {
        font-size: 32px;
        color: #ffffff;
      }
    }

    &:hover .upload-overlay {
      opacity: 1;
    }
  }

  .upload-label {
    font-family: Noto Looped Thai UI, Noto Looped Thai UI;
    font-weight: 500;
    font-size: 13px;
    color: #696969;
    line-height: 17px;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
}
</style>
